import { cn } from '@/lib/utils';
import type { ClinicalAssessmentSource } from '@/ayurveda/types/ayurveda';

/**
 * Color scheme for the chip's selected state, matching the Figma palette:
 * - sage: Astha Sthaana Pareeksha (Nadi/Mootram/Malam/Jihva/Sabdam/Sparsham/Drik/Akrithi)
 * - purple: Prakruthi
 * - gold: Dosha
 * - coral: Aama
 */
export type ClinicalAssessmentChipColor = 'sage' | 'purple' | 'gold' | 'coral';

const SELECTED_COLOR_CLASSES: Record<ClinicalAssessmentChipColor, string> = {
  sage: 'border-[#A3B28B] bg-[#A3B28B] text-[#FFFCEB]',
  purple: 'border-[#D6B0D3] bg-[#D6B0D3] text-[#F6F4FD]',
  gold: 'border-[#D8C080] bg-[#D8C080] text-white',
  coral: 'border-[#EDA593] bg-[#EDA593] text-white',
};

const UNSELECTED_COLOR_CLASSES: Record<ClinicalAssessmentChipColor, string> = {
  sage: 'border-transparent bg-[#F8F8F1] text-[#585859] hover:bg-[#EFEFDF]',
  purple: 'border-transparent bg-[#F9F0FF] text-[#585859] hover:bg-[#F0E0FA]',
  gold: 'border-transparent bg-[#FDF8EB] text-[#585859] hover:bg-[#F7ECD3]',
  coral: 'border-transparent bg-[#FFEFEB] text-[#585859] hover:bg-[#FCE1DA]',
};

export interface ClinicalAssessmentChipProps {
  label: string;
  selected: boolean;
  color?: ClinicalAssessmentChipColor;
  /** Only meaningful when `selected` is true — how this value was set. */
  source?: ClinicalAssessmentSource | null;
  disabled?: boolean;
  onClick?: () => void;
}

const SOURCE_LABEL: Record<ClinicalAssessmentSource, string> = {
  pa_soap: 'From kiosk intake',
  voice: 'From consultation',
  manual: 'Confirmed by Vaidya',
};

export function ClinicalAssessmentChip({
  label,
  selected,
  color = 'sage',
  source,
  disabled,
  onClick,
}: ClinicalAssessmentChipProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={selected && source ? SOURCE_LABEL[source] : undefined}
      className={cn(
        'rounded-xl border px-3 py-1 font-hanken text-lg font-normal leading-[26px] transition-colors',
        selected ? SELECTED_COLOR_CLASSES[color] : UNSELECTED_COLOR_CLASSES[color],
        disabled && 'cursor-not-allowed opacity-60'
      )}
    >
      {label}
      {selected && source === 'manual' && <span className="ml-1">✓</span>}
    </button>
  );
}
